import { useForm, zodResolver } from '@mantine/form';
import { cloneDeep, omit } from 'lodash';
import { ChangeEvent } from 'react';

import { Declaration, DeclarationGoods } from '@e-origin/shared';

import { useUpdatePartialDeclaration } from '@hooks/declaration';
import { DeclarationViewGoodHVFormValues, declarationViewGoodHVSchema } from './validateDeclarationViewGoodHVForm';

interface DeclarationViewGoodHVFormInitializerProps {
  declaration: Declaration;
  goodDetails: DeclarationGoods;
  onHide(): void;
}

export const useDeclarationViewGoodHVForm = ({ declaration, goodDetails, onHide }: DeclarationViewGoodHVFormInitializerProps) => {
  const savePartialDeclarationMutation = useUpdatePartialDeclaration();

  const form = useForm<DeclarationViewGoodHVFormValues>({
    initialValues: {
      goodShipmentGlobal: {
        totalQuantitySupplementaryUnits: declaration.goodShipmentGlobal.totalQuantitySupplementaryUnits,
        firstItemData: {
          nationalAdditionalCode: declaration.goodShipmentGlobal.firstItemData.nationalAdditionalCode,
          nationalAdditionalCode2: declaration.goodShipmentGlobal.firstItemData.nationalAdditionalCode2,
          nationalAdditionalCode3: declaration.goodShipmentGlobal.firstItemData.nationalAdditionalCode3,
          taricAdditionalCode: declaration.goodShipmentGlobal.firstItemData.taricAdditionalCode,
          taricAdditionalCode2: declaration.goodShipmentGlobal.firstItemData.taricAdditionalCode2,
          hsCode: declaration.goodShipmentGlobal.firstItemData.hsCode,
          typeOfGoods: declaration.goodShipmentGlobal.firstItemData.typeOfGoods,
          cusCode: declaration.goodShipmentGlobal.firstItemData.cusCode,
          invoiceDescription: declaration.goodShipmentGlobal.firstItemData.invoiceDescription,
        },
        totalQuantity: declaration.goodShipmentGlobal.totalQuantity,
        totalNumberOfPackages: declaration.goodShipmentGlobal.totalNumberOfPackages,
        totalNetMass: declaration.goodShipmentGlobal.totalNetMass,
        totalGrossMass: declaration.goodShipmentGlobal.totalGrossMass,
        totalAmountInvoiced: declaration.goodShipmentGlobal.totalAmountInvoiced,
      },
      otherDataElements: {
        quotaOrderNumber: declaration.otherDataElements.quotaOrderNumber,
      },
      consignment: {
        countryOfOrigin: declaration.consignment.countryOfOrigin,
        regionOfOrigin: declaration.consignment.regionOfOrigin,
        countryOfPreferentialOrigin: declaration.consignment.countryOfPreferentialOrigin,
      },
      declarationGoods: {
        evalMethodAiHsCode: {
          taricSupplementaryUnits: declaration.goodsShipmentItems[0].evalMethodAiHsCode.taricSupplementaryUnits,
          codeQualifyer: (declaration.goodsShipmentItems[0].evalMethodAiHsCode.taricSupplementaryUnits || [])[0]?.codeQualifyer,
        },
        dutiesAndTaxes: goodDetails.dutiesAndTaxes,
        dangerousGoods: {
          UNDG:
            (goodDetails as any).dangerousGoods?.UNDG && (goodDetails as any).dangerousGoods.UNDG.length > 0 ? (goodDetails as any).dangerousGoods.UNDG : [''],
        },
      },
    },
    validate: zodResolver(declarationViewGoodHVSchema),
  });

  const onSubmit = async (values) => {
    await savePartialDeclarationMutation.mutateAsync({
      id: declaration._id,
      partialDeclaration: omit(values, 'declarationGoods'),
      partialGoodsShipmentItem: {
        ...values.declarationGoods,
        commodityCode: {
          nationalAdditionalCode: values.goodShipmentGlobal.firstItemData.nationalAdditionalCode,
        },
        dangerousGoods: values.declarationGoods.dangerousGoods,
      },
      storeSavedDeclaration: true,
    });
    onHide();
  };

  const handleTax100 = (event: ChangeEvent<HTMLInputElement>) => {
    const { dutiesAndTaxes } = form.values.declarationGoods;
    let tax100 = dutiesAndTaxes?.items?.find((item) => item.taxType === '100');

    if (!tax100) {
      tax100 = { taxType: '100' };
      dutiesAndTaxes?.items.push(tax100);
    }

    if (event.target.value) {
      tax100.taxBase = [{ measurementUnitAndQualifier: event.target.value }];
    } else {
      dutiesAndTaxes.items = dutiesAndTaxes?.items.filter((item) => item.taxType !== '100');
    }
    form.setFieldValue('declarationGoods.dutiesAndTaxes', dutiesAndTaxes);
  };

  const handleTaricSupplementaryUnits = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let { taricSupplementaryUnits } = form.values.declarationGoods.evalMethodAiHsCode;
    taricSupplementaryUnits = cloneDeep(taricSupplementaryUnits) || [];

    const firstTaricSupplementaryUnit = {
      ...taricSupplementaryUnits[0],
      codeQualifyer: value,
    };

    if (!taricSupplementaryUnits[0]) {
      taricSupplementaryUnits.unshift(firstTaricSupplementaryUnit);
    } else {
      taricSupplementaryUnits[0] = firstTaricSupplementaryUnit;
    }

    form.setFieldValue('declarationGoods.evalMethodAiHsCode.taricSupplementaryUnits', taricSupplementaryUnits);
    form.setFieldValue('declarationGoods.evalMethodAiHsCode.codeQualifyer', value);
  };

  const handleSupplementaryTotalQuantity = () => {
    const codeQualifyer = (declaration.goodsShipmentItems[0].evalMethodAiHsCode?.taricSupplementaryUnits || [])[0]?.codeQualifyer
      ?.split(' ')
      ?.join('')
      ?.trim()
      ?.slice(0, 4);

    if (!codeQualifyer) {
      return undefined;
    }

    let supplementaryUnits = declaration.goodShipmentGlobal.totalQuantity;

    if (declaration.goodShipmentGlobal.totalQuantitySupplementaryUnits) {
      supplementaryUnits = declaration.goodShipmentGlobal.totalQuantitySupplementaryUnits;
    } else if (['KGM', 'LTR'].includes(codeQualifyer)) {
      supplementaryUnits = declaration.goodShipmentGlobal.totalNetMass;
    } else if (codeQualifyer === 'GRM') {
      supplementaryUnits = declaration.goodShipmentGlobal.totalNetMass * 1000;
    }

    return supplementaryUnits;
  };

  return {
    form,
    onSubmit,
    handleTax100,
    handleTaricSupplementaryUnits,
    handleSupplementaryTotalQuantity,
    isSaving: savePartialDeclarationMutation.isPending,
  };
};
